import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild } from '@angular/core';
import { User, UserService } from '../core/user/user.service';
import { objectPropertiesToArray } from '@rallycommerce/common/utils.js';
import { Breakpoint, CaseType, convertCamelCaseToSnakeCase, fromMediaQuery } from '../core/helpers/utils';
import { environment } from '../../environments/environment';
import { AgentService } from '../core/agent/agent.service';
import { NgxSmartModalComponent, NgxSmartModalService } from 'ngx-smart-modal';
import { SubscriptionLayout, SubscriptionService } from '../core/subscription/subscription.service';
import { GuidedSetupService } from '../guided-setup/guided-setup.service';
import { DataStorage } from '../core/data/data-storage';
import { Feature, SubscriptionPlanName } from '../core/subscription/subscription';
import { Subscription } from 'rxjs';
import { InternalAnalyticsService, AnalyticsEvent } from '../core/internal-analytics.service';
import { Router } from '@angular/router';
import { SessionService } from '../core/session.service';
import { BusinessService } from '../core/business/business.service';
import { TransferDepartmentType } from '../core/business/business';
@Component({
  selector: 'rosie-nav-sidebar',
  templateUrl: './nav-sidebar.component.html',
  styleUrls: ['./nav-sidebar.component.scss'],
  standalone: false
})
export class NavSidebarComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('trialExpiredModal') trialExpiredModal: NgxSmartModalComponent;
  NavSidebarItem = NavSidebarItem;
  persistentStorage: DataStorage = DataStorage.persistent();
  subscriptionData: { feature?: Feature, plan?: SubscriptionPlanName, origin?: SubscriptionLayout } | null = null;
  isMobileVersion: boolean = false;
  isExpanded: boolean = false;
  navSidebarItems: NavSidebar[];
  private hoverState: { [key: string]: boolean } = {};
  private subscriptions: Subscription[] = [];
  private hasAnsweredReferralSource: boolean | null = null;

  constructor(
    private user: UserService,
    private subscription: SubscriptionService,
    private modal: NgxSmartModalService,
    private setup: GuidedSetupService,
    private analytics: InternalAnalyticsService,
    private router: Router,
    private session: SessionService,
    private business: BusinessService,
  ) { }

  ngOnInit() {
    this.navSidebarItems = this.generateNavSidebarItems();

    const resizeSub = fromMediaQuery(Breakpoint.UpToMedium).subscribe(matches => {
      this.isMobileVersion = matches;
    });

    const refreshStateSub = this.session.refreshState.subscribe(() => {
      this.handleTrialExpiredModalState();
    });

    this.subscriptions.push(resizeSub, refreshStateSub);

    if (this.isGuidedSetupCompleted) {
      this.persistentStorage.remove('isAwaitingInitialSetup');
    }
  }

  ngAfterViewInit() {
    const openSub = this.subscription.open.subscribe((data) => {
      this.openSubscriptionModal(data);
    });

    const closeSub = this.subscription.close.subscribe(() => {
      this.handleCloseSubscriptionModal();
    });

    const createdSub = this.subscription.created.subscribe(() => {
      this.handleCloseSubscriptionModal();
    });

    this.subscriptions.push(openSub, closeSub, createdSub);

    if (this.user.hasTrialExpired) {
      this.trialExpiredModal.open();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  get hasHideMenuParam(): boolean {
    return new URLSearchParams(window.location.search).get('menu') === 'false';
  }

  get showBillingAlert(): boolean {
    if (this.subscription.isEnterprisePlanSelected && !this.subscription.isExternalBillingProvider) {
      return false;
    }

    if (this.subscription.isPendingApproval) {
      return true;
    }

    if ((this.subscription.isCanceled || this.subscription.isExpired) && this.subscription.isExternalBillingProvider) {
      return true;
    }

    const isSetupCompleted = !!this.setup.isGuidedSetupCompleted && !this.isUserOnTrialBasedBilling;
    const isCreditCardMissing = (isSetupCompleted) && !this.subscription.hasCreditCardAdded;
    const isPastDue = this.subscription.isPastDue;
    const isUserOnInitialBillingVersion = !this.user.isUserOnFreeMinutesBillingVersion;
    return (isCreditCardMissing || isPastDue) && !!this.user.userData && isUserOnInitialBillingVersion;
  }

  get showNavSidebar(): boolean {
    // Match the desktop sidebar: hide the mobile burger on every guided-setup
    // step before Go Live, for every variant.
    if (this.setup.shouldHideNavForGuidedSetup) {
      return false;
    }

    if (this.user.hasCommerceApp) {
      return this.setup.isTestStepCompleted
        || this.setup.isGuidedSetupCompleted
        || this.router.url.includes('/conversations;id=')
        || this.router.url.includes('/calls;id=');
    }

    return true;
  }

  get hasTrialExpired(): boolean {
    return this.user.hasTrialExpired;
  }

  get isGuidedSetupCompleted(): boolean {
    return this.setup.isGuidedSetupCompleted;
  }

  // The usage box and feature cards become relevant once the user reaches the
  // test-call step (ENG-2207) - not only after Go Live.
  get hasReachedTestCallStep(): boolean {
    return this.setup.hasReachedTestCallStep;
  }

  get hasMultipleBusinesses(): boolean {
    return this.user.hasMultipleBusinesses;
  }

  get userInformation(): User {
    return this.user.userData;
  }

  get isProduction(): boolean {
    return environment.name === 'production' || environment.name === 'sandbox';
  }

  get businessTitle(): string {
    return this.user.activeBusiness?.title;
  }

  get hasCreditCardOnFile(): boolean {
    return this.subscription.hasCreditCardAdded;
  }

  get featureName(): string {
    if (this.subscriptionData?.feature) {
      const featureNames = {
        [Feature.TransferCalls]: 'Transfer Calls',
        [Feature.TrainingFiles]: 'Training Files',
        [Feature.Appointments]: 'Appointment Links',
        [Feature.TextLinks]: 'Text a Link',
        [Feature.MessageScenarios]: 'Message Taking Scenarios'
      };
      return featureNames[this.subscriptionData.feature];
    }
    return '';
  }

  get isUserOnFreeMinutesBillingVersion(): boolean {
    return this.user.isUserOnFreeMinutesBillingVersion;
  }

  get isUserOn7DayFreeTrialVersion(): boolean {
    return this.user.isUserOn7DayFreeTrialVersion;
  }

  get isUserOnTrialBasedBilling(): boolean {
    return this.user.isUserOnTrialBasedBilling;
  }

  get hasCreditCardAdded(): boolean {
    return this.subscription.hasCreditCardAdded;
  }

  get isCanceled(): boolean {
    return this.subscription.isCanceled || this.subscription.isExpired;
  }

  get shouldShowAppBanner(): boolean {
    return this.setup.hasReachedTestCallStep && !this.business.isSidebarBannerDismissed;
  }

  get shouldShowWebsiteChatBanner(): boolean {
    return this.setup.hasReachedTestCallStep && !this.business.isWebsiteChatBannerDismissed;
  }

  get shouldShowCrmIntegrationsBanner(): boolean {
    return this.setup.hasReachedTestCallStep && !this.business.isCrmIntegrationsBannerDismissed;
  }

  get shouldShowLeadSourcesBanner(): boolean {
    return this.setup.hasReachedTestCallStep && !this.business.isLeadSourcesBannerDismissed;
  }

  get shouldShowWebsiteTextingBanner(): boolean {
    return this.setup.hasReachedTestCallStep && !this.business.isWebsiteTextingBannerDismissed;
  }

  get hasWaterfallTransferScenario(): boolean {
    return (this.user.activeBusiness?.transferDepartments || [])
      .some(department => department.type === TransferDepartmentType.Waterfall);
  }

  get shouldShowWaterfallTransfersBanner(): boolean {
    const hasReachedTest = this.setup.hasReachedTestCallStep;
    return hasReachedTest && !this.hasWaterfallTransferScenario && !this.business.isWaterfallTransfersBannerDismissed;
  }

  get shouldShowMessageTakingBanner(): boolean {
    const hasReachedTest = this.setup.hasReachedTestCallStep;
    const isLegacy = this.user.userData?.agent?.configuration?.hasMessageScenariosEnabled !== true;
    return hasReachedTest && isLegacy && !this.business.isMessageTakingBannerDismissed;
  }

  onCardDismiss(cardId: string): void {
    if (cardId === 'waterfall-transfers') {
      this.business.dismissWaterfallTransfersBanner().subscribe();
    } else if (cardId === 'app-download') {
      this.business.dismissSidebarBanner().subscribe();
    } else if (cardId === 'website-texting') {
      this.business.dismissWebsiteTextingBanner().subscribe();
    } else if (cardId === 'website-chat') {
      this.business.dismissWebsiteChatBanner().subscribe();
    } else if (cardId === 'lead-sources') {
      this.business.dismissLeadSourcesBanner().subscribe();
    } else if (cardId === 'crm-integrations') {
      this.business.dismissCrmIntegrationsBanner().subscribe();
    } else if (cardId === 'message-taking') {
      this.business.dismissMessageTakingBanner().subscribe();
    }
  }

  get shouldShowFeedback(): boolean {
    if (this.user.userData?.referralSource) {
      return false;
    }

    const isLaunchStepViewed = this.setup.isLaunchStepViewed;
    const isGuidedSetupCompleted = this.setup.isGuidedSetupCompleted;
    return isLaunchStepViewed || isGuidedSetupCompleted;
  }

  onMouseEnter(key: NavSidebarItem): void {
    this.hoverState[key] = true;
  }

  onMouseLeave(key: NavSidebarItem): void {
    this.hoverState[key] = false;
  }

  isHovered(key: NavSidebarItem): boolean {
    return !!this.hoverState[key];
  }

  handleSelection(item: NavSidebarItem) {
    if (this.isMobileVersion) {
      this.isExpanded = false;
    }
    this.analytics.trackEvent(AnalyticsEvent.NavigationItemAction, { item });
  }

  toggleMenu() {
    this.isExpanded = !this.isExpanded;
  }

  onClickOutside() {
    if (this.isMobileVersion && this.isExpanded) {
      this.isExpanded = false;
    }
  }

  getTranslationKey(key: string): string {
    return `SIDEBAR.${key.replace(/[^a-zA-Z0-9 ]/g, '')?.toUpperCase()}`;
  }

  getKebabCaseKey(key: string): string {
    return convertCamelCaseToSnakeCase(key, CaseType.LowerCase).replace(/_/g, '-');
  }

  getIcon(key: NavSidebarItem): string {
    return icons[key];
  }

  logout() {
    this.user.logout();
  }

  shouldShowNavItem(item: NavSidebarItem): boolean {
    // TODO: show sidebar or not to show sidebar, this is the question
    if (this.setup.isGuidedSetupCompleted || true) {
      return true;
    }
    if (item === NavSidebarItem.Setup) {
      return true;
    }
    const itemRoute = '/' + this.getKebabCaseKey(item);
    return this.router.url.startsWith(itemRoute);
  }

  handleSubscriptionCancel() {
    this.handleCloseSubscriptionModal();
  }

  extendTrial() {
    this.trialExpiredModal.close();
    this.openSubscriptionModal({ plan: SubscriptionPlanName.Scale });
  }

  private handleTrialExpiredModalState() {
    if (this.user.hasTrialExpired) {
      if (!this.trialExpiredModal.isVisible()) {
        this.trialExpiredModal.open();
      }
    } else {
      if (this.trialExpiredModal.isVisible()) {
        this.modal.closeAll();
      }
    }
  }

  private openSubscriptionModal(data: { feature?: Feature, plan?: SubscriptionPlanName, origin?: SubscriptionLayout } | null) {
    if (this.subscription.isShopifyBillingProvider) {
      // Shopify plan changes happen on Shopify, not our billing page. When the
      // upgrade CTA knows which plan it needs, send the merchant straight to
      // Shopify's approval screen for it; otherwise fall back to the billing page.
      const planKey = data?.plan ? data.plan.toString().toLowerCase() : null;
      if (planKey) {
        this.subscription.changeProviderPlan(planKey).subscribe({
          next: (result) => {
            if (result?.confirmationUrl) {
              window.open(result.confirmationUrl, '_top');
            } else {
              this.router.navigate(['/account', { section: 'billing' }]);
            }
          },
          error: () => this.router.navigate(['/account', { section: 'billing' }])
        });
        return;
      }
      this.router.navigate(['/account', { section: 'billing' }]);
      return;
    }

    this.subscriptionData = data;
    this.subscription.layout = this.subscriptionData?.origin;
    const modalInstance = this.modal.getModal('subscriptionModal');
    modalInstance.open().onOpenFinished.subscribe(() => {
      this.subscription.initialize.next(true);
      if (this.subscriptionData?.feature) {
        this.subscription.upgradeToPlanActive = this.subscriptionData?.plan;
        this.subscription.selectedPlanChanged.next(this.subscriptionData?.plan);
      }
    });
  }

  private generateNavSidebarItems(): NavSidebar[] {
    return objectPropertiesToArray(NavSidebarItem).map((key: NavSidebarItem) => {
      return { key };
    });
  }

  private handleCloseSubscriptionModal() {
    this.modal.getModal('subscriptionModal').close();
    this.subscription.reset.next(true);
    this.subscriptionData = null;
  }
}

interface NavSidebar {
  key: NavSidebarItem;
  additionalIcon?: string;
  tooltipTranslationKey?: string;
}

enum NavSidebarItem {
  Setup = 'setup',
  Conversations = 'conversations',
  Settings = 'settings',
  Integrations = 'integrations',
  Account = 'account'
}

const icons = {
  [NavSidebarItem.Setup]: 'heroDocumentCheck',
  [NavSidebarItem.Conversations]: 'heroInboxArrowDown',
  [NavSidebarItem.Settings]: 'heroCog6Tooth',
  [NavSidebarItem.Integrations]: 'heroSquaresPlus',
  [NavSidebarItem.Account]: 'heroUserCircle'
}